import {
  BRAND,
  BRAND_SHORT,
  CONTACT_EMAIL,
  CURRENCY,
  LOGO_PATH,
  SOCIAL_PROFILES,
} from "./config.mjs";

// Organization deliberately carries NO postalAddress. The markup used to claim
// addressCountry: "KE", which is where the software was written, not where the
// business operates. A wrong address is worse than an absent one - address is
// optional on Organization, and Google resolves the entity from url + logo +
// sameAs regardless. Add the real US address here once it is confirmed.
export const organizationNode = (origin) => ({
  "@type": "Organization",
  "@id": `${origin}/#organization`,
  name: BRAND,
  description: "Small-batch artisan perfume house, handcrafted in the USA.",
  url: `${origin}/`,
  logo: {
    "@type": "ImageObject",
    url: `${origin}${LOGO_PATH}`,
    width: 512,
    height: 512,
  },
  sameAs: SOCIAL_PROFILES,
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    email: CONTACT_EMAIL,
    areaServed: "US",
    availableLanguage: "en",
  },
});

// WebSite is kept, but WITHOUT potentialAction/SearchAction: Google retired the
// sitelinks searchbox on 21 Nov 2024, so that markup earns nothing. WebSite
// itself still feeds the site-name feature, which is worth having.
export const webSiteNode = (origin) => ({
  "@type": "WebSite",
  "@id": `${origin}/#website`,
  url: `${origin}/`,
  name: BRAND,
  publisher: { "@id": `${origin}/#organization` },
});

export const breadcrumbNode = (origin, path, trail) => ({
  "@type": "BreadcrumbList",
  "@id": `${origin}${path}#breadcrumb`,
  itemListElement: trail.map((crumb, index) => {
    const item = {
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
    };
    // The last crumb is the current page and carries no `item` - that is what
    // tells Google which node it is looking at.
    if (crumb.path) item.item = `${origin}${crumb.path}`;
    return item;
  }),
});

const twoDecimals = (value) => Number(value).toFixed(2);

const isoDatePlusYear = (now = new Date()) => {
  const date = new Date(now.getTime());
  date.setFullYear(date.getFullYear() + 1);
  return date.toISOString().slice(0, 10);
};

export const productNode = ({ origin, path, product, images, reviews = [] }) => {
  const node = {
    "@type": "Product",
    "@id": `${origin}${path}#product`,
    name: product.name,
    url: `${origin}${path}`,
    // The UUID until a real SKU column exists. Stable and unique, which is all
    // `sku` is required to be. gtin/mpn are omitted rather than invented.
    sku: String(product.id),
    brand: { "@type": "Brand", name: BRAND_SHORT },
  };

  if (product.description) node.description = product.description;
  if (images.length > 0) node.image = images;
  if (product.category?.name) node.category = product.category.name;

  const properties = [];
  if (product.scentsProfile) {
    properties.push({
      "@type": "PropertyValue",
      name: "Scent profile",
      value: product.scentsProfile,
    });
  }
  if (product.size) {
    properties.push({
      "@type": "PropertyValue",
      name: "Size",
      value: `${product.size} ml`,
    });
  }
  if (properties.length > 0) node.additionalProperty = properties;

  // An AggregateRating with reviewCount 0 is a hard structured-data error and
  // suppresses the entire rich result, so it is emitted only when real reviews
  // exist. averageRating/reviewCount already ship on every product payload.
  if (product.reviewCount > 0 && product.averageRating != null) {
    node.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: String(Number(product.averageRating).toFixed(1)),
      reviewCount: Number(product.reviewCount),
      bestRating: "5",
      worstRating: "1",
    };
  }

  const reviewNodes = reviews.slice(0, 5).map((review) => {
    const reviewNode = {
      "@type": "Review",
      author: { "@type": "Person", name: review.reviewerName || "Verified buyer" },
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(review.rating),
        bestRating: "5",
        worstRating: "1",
      },
    };
    if (review.title) reviewNode.name = review.title;
    if (review.body) reviewNode.reviewBody = review.body;
    // The reviews endpoint pre-formats createdAt as "14 July 2026", which is not
    // ISO 8601. Parsing an English month name back into a date is fragile, so
    // datePublished is emitted only once the backend exposes createdAtIso.
    // It is a recommended property, not a required one, so omitting it is safe.
    if (review.createdAtIso) reviewNode.datePublished = review.createdAtIso;
    return reviewNode;
  });
  if (reviewNodes.length > 0) node.review = reviewNodes;

  const price = product.hasDiscount ? product.discountedPrice : product.price;

  // Merchant listing experiences require price > 0. Emitting "0.00" fails
  // validation outright, so the whole offer is dropped instead.
  if (price != null && Number(price) > 0) {
    node.offers = {
      "@type": "Offer",
      "@id": `${origin}${path}#offer`,
      url: `${origin}${path}`,
      priceCurrency: CURRENCY,
      price: twoDecimals(price),
      priceValidUntil: isoDatePlusYear(),
      itemCondition: "https://schema.org/NewCondition",
      // Never claim InStock for a known-zero stock: it is a Merchant policy
      // violation and it produces angry customers.
      availability:
        product.stock === 0
          ? "https://schema.org/OutOfStock"
          : "https://schema.org/InStock",
      seller: { "@id": `${origin}/#organization` },
    };
    // shippingDetails and hasMerchantReturnPolicy are intentionally absent.
    // Google may surface those values directly in the SERP, so they go in only
    // once the real shipping windows and return terms are confirmed.
  }

  return node;
};

export const collectionNode = ({ origin, path, name, description, products }) => {
  const node = {
    "@type": "CollectionPage",
    "@id": `${origin}${path}#collection`,
    url: `${origin}${path}`,
    name,
    isPartOf: { "@id": `${origin}/#website` },
  };
  if (description) node.description = description;

  if (products?.length > 0) {
    node.mainEntity = {
      "@type": "ItemList",
      numberOfItems: products.length,
      itemListElement: products.slice(0, 30).map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${origin}/product/${product.id}`,
        name: product.name,
      })),
    };
  }

  return node;
};

export const graph = (nodes) => ({
  "@context": "https://schema.org",
  "@graph": nodes.filter(Boolean),
});
